import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Accessibility,
  X,
  Type,
  Contrast,
  Underline,
  Pause,
  RotateCcw,
  BookOpen,
} from "lucide-react";

const STORAGE_KEY = "tiob-a11y-prefs";

const defaults = {
  largeText: false,
  highContrast: false,
  readableFont: false,
  underlineLinks: false,
  stopMotion: false,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(defaults);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved) setPrefs(saved);
    } catch {}
  }, []);

  useEffect(() => {
    const body = document.body;
    body.classList.toggle("a11y-large-text", prefs.largeText);
    body.classList.toggle("a11y-high-contrast", prefs.highContrast);
    body.classList.toggle("a11y-readable-font", prefs.readableFont);
    body.classList.toggle("a11y-underline-links", prefs.underlineLinks);
    body.classList.toggle("a11y-stop-motion", prefs.stopMotion);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  const reset = () => setPrefs(defaults);

  const Item = ({ active, icon: Icon, label, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
        active
          ? "border-[var(--blue)] bg-[var(--blue)]/10 text-[var(--blue-ink)]"
          : "border-[var(--line)] hover:border-[var(--blue)] hover:bg-[var(--blue)]/5 text-[var(--ink)]"
      }`}
    >
      <Icon size={22} className={active ? "text-[var(--blue-deep)]" : ""} />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );

  return (
    <>
      {/* Floating launcher (bottom-left) - handicap icon, blue, prominent */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open accessibility menu"
        data-testid="a11y-widget-launcher"
        className="fixed bottom-5 left-5 z-40 w-14 h-14 rounded-full bg-[var(--blue)] text-white shadow-[0_12px_30px_-8px_rgba(29,143,225,0.55)] hover:scale-110 hover:bg-[var(--blue-deep)] transition-all flex items-center justify-center"
      >
        <Accessibility size={26} strokeWidth={2.4} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-start" role="dialog" aria-modal="true" aria-label="Accessibility settings" data-testid="a11y-widget-panel">
          <div className="fixed inset-0 bg-[var(--blue-ink)]/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white w-full md:w-[420px] md:ml-6 md:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto border border-[var(--line)]">
            <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
              <div>
                <div className="eyebrow">★ Accessibility</div>
                <h2 className="font-display text-xl text-[var(--blue-ink)] mt-1">Customize Your Experience</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close accessibility menu" className="p-2 rounded-full hover:bg-[var(--line)]" data-testid="a11y-widget-close">
                <X size={22} />
              </button>
            </div>

            <div className="p-5 grid grid-cols-2 gap-3">
              <Item active={prefs.largeText} icon={Type} label="Larger Text" onClick={() => toggle("largeText")} testId="a11y-toggle-large-text" />
              <Item active={prefs.highContrast} icon={Contrast} label="High Contrast" onClick={() => toggle("highContrast")} testId="a11y-toggle-contrast" />
              <Item active={prefs.readableFont} icon={BookOpen} label="Readable Font" onClick={() => toggle("readableFont")} testId="a11y-toggle-readable-font" />
              <Item active={prefs.underlineLinks} icon={Underline} label="Underline Links" onClick={() => toggle("underlineLinks")} testId="a11y-toggle-underline" />
              <Item active={prefs.stopMotion} icon={Pause} label="Pause Motion" onClick={() => toggle("stopMotion")} testId="a11y-toggle-stop-motion" />
              <button type="button" onClick={reset} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-[var(--line)] hover:border-[var(--blue-ink)] hover:bg-[var(--cream)] text-[var(--ink)]" data-testid="a11y-reset">
                <RotateCcw size={22} />
                <span className="text-xs font-medium">Reset</span>
              </button>
            </div>

            <div className="p-5 border-t border-[var(--line)] bg-[var(--cream)] text-sm text-[var(--ink-soft)]">
              <p className="mb-3">
                We are committed to WCAG 2.1 Level AA. If you experience any difficulty accessing
                our website, please{" "}
                <Link to="/contact" className="text-[var(--blue-deep)] underline font-medium" onClick={() => setOpen(false)}>contact us</Link>
                {" "}- we'll respond promptly.
              </p>
              <Link to="/accessibility" className="inline-flex items-center gap-2 font-semibold text-[var(--blue-ink)] hover:text-[var(--blue-deep)]" onClick={() => setOpen(false)} data-testid="a11y-statement-link">
                Read full Accessibility Statement →
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
